import React, { Suspense } from 'react';
import { Outlet, Route, Routes as RouterRoutes } from 'react-router-dom';
import PrivateRoutes from './PrivateRoutes';
import SenseConfigurationProvider from 'Providers/SenseConfigurationProvider';
import { SidebarProvider } from 'Providers/SidebarProvider';
import AppLayout from 'Components/AppLayout/AppLayout';
import { CircularProgress, Box } from '@mui/material';

// Eager load critical routes
import Login from 'Pages/Auth/pages/LoginPage';
import Dashboard from 'Pages/Dashboard/Dashboard';
import AuthLayout from 'Pages/Auth/components/AuthLayout';

const PasswordReset = React.lazy(
  () => import('Pages/Auth/pages/PasswordResetPage')
);

// Lazy load other routes
const StatsDetailsPage = React.lazy(
  () => import('~/features/stats-details/pages/stats-details-page')
);
const LiftsPage = React.lazy(() => import('~/features/lifts/pages/lifts-page'));
const LiftDetails = React.lazy(() => import('Pages/LiftDetails/LiftDetails'));
const PasswordRecovery = React.lazy(
  () => import('Pages/Auth/pages/PasswordRecoveryPage')
);
const AlarmCalls = React.lazy(() => import('Pages/AlarmCalls/AlarmCalls'));
const EventsPage = React.lazy(() => import('Pages/EventsPage/EventsPage'));
const CurrentCalls = React.lazy(
  () => import('Pages/CurrentCalls/CurrentCalls')
);
const EventPage = React.lazy(() => import('Pages/EventPage/EventPage'));
const Subscriptions = React.lazy(
  () => import('Pages/Subscriptions/Subscriptions')
);
const ErrorPage = React.lazy(() => import('Pages/ErrorPage/ErrorPage'));

// Loading component
export const PageLoader = () => (
  <Box
    display="flex"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
  >
    <CircularProgress />
  </Box>
);

const Routes = () => {
  return (
    <RouterRoutes>
      <Route
        element={
          <AuthLayout>
            <Outlet />
          </AuthLayout>
        }
      >
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<Login />} />
        <Route path="/password-reset" element={<PasswordReset />} />
      </Route>

      <Route
        path="/password-reset/:token"
        element={
          <Suspense fallback={<PageLoader />}>
            <PasswordRecovery />
          </Suspense>
        }
      />

      {/* Layout wrapper for authenticated routes with persistent sidebar */}
      <Route
        element={
          <PrivateRoutes>
            <SenseConfigurationProvider>
              <SidebarProvider>
                <AppLayout>
                  <Outlet />
                </AppLayout>
              </SidebarProvider>
            </SenseConfigurationProvider>
          </PrivateRoutes>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route
          path="/current-calls"
          element={
            <Suspense fallback={<PageLoader />}>
              <CurrentCalls />
            </Suspense>
          }
        />
        <Route
          path="/lifts/:liftId"
          element={
            <Suspense fallback={<PageLoader />}>
              <LiftDetails />
            </Suspense>
          }
        />
        <Route
          path="/lifts"
          element={
            <Suspense fallback={<PageLoader />}>
              <LiftsPage />
            </Suspense>
          }
        />
        <Route
          path="/alarm-calls"
          element={
            <Suspense fallback={<PageLoader />}>
              <AlarmCalls />
            </Suspense>
          }
        />
        <Route
          path="/subscriptions"
          element={
            <Suspense fallback={<PageLoader />}>
              <Subscriptions />
            </Suspense>
          }
        />
        <Route
          path="/stats-details/:type"
          element={
            <Suspense fallback={<PageLoader />}>
              <StatsDetailsPage />
            </Suspense>
          }
        />
        <Route
          path="/events"
          element={
            <Suspense fallback={<PageLoader />}>
              <EventsPage />
            </Suspense>
          }
        />
        <Route
          path="/events/:eventId"
          element={
            <Suspense fallback={<PageLoader />}>
              <EventPage />
            </Suspense>
          }
        />
      </Route>

      <Route
        path="*"
        element={
          <Suspense fallback={<PageLoader />}>
            <ErrorPage />
          </Suspense>
        }
      />
    </RouterRoutes>
  );
};

export default Routes;
